import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Globe, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

function getBadgeColor(fact) {
  if (!fact) return 'bg-slate-100 text-slate-500';
  if (fact.source === 'manual_override') return 'bg-emerald-100 text-emerald-700';
  if (fact.confidence === 'high') return 'bg-blue-100 text-blue-700';
  if (fact.confidence === 'medium') return 'bg-yellow-100 text-yellow-700';
  return 'bg-slate-100 text-slate-500';
}

function getBadgeLabel(fact) {
  if (!fact) return 'auto';
  if (fact.source === 'manual_override') return 'override';
  return fact.confidence || 'auto';
}

function getPageColor(pageType) {
  const colors = {
    girls_profile: 'bg-pink-100 text-pink-700',
    prices: 'bg-amber-100 text-amber-700',
    contact: 'bg-blue-100 text-blue-700',
    services: 'bg-purple-100 text-purple-700',
    about: 'bg-teal-100 text-teal-700',
    faq: 'bg-orange-100 text-orange-700',
    gallery: 'bg-indigo-100 text-indigo-700',
    hours: 'bg-cyan-100 text-cyan-700',
    booking: 'bg-rose-100 text-rose-700',
  };
  return colors[pageType] || 'bg-slate-100 text-slate-500';
}

function getFactValue(fact) {
  if (!fact) return '';
  var val = fact.value !== undefined ? fact.value : fact;
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function AnalyzeButton({ analyzing, analyzeStatus, websiteUrl, onAnalyze }) {
  if (analyzing) {
    return (
      <Button onClick={onAnalyze} disabled data-testid="button-analyze-website">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Analisi...
      </Button>
    );
  }
  if (analyzeStatus === 'success') {
    return (
      <Button onClick={onAnalyze} disabled={!websiteUrl} variant="outline" data-testid="button-analyze-website">
        <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
        Rianalizza
      </Button>
    );
  }
  return (
    <Button onClick={onAnalyze} disabled={!websiteUrl} data-testid="button-analyze-website">
      Analizza Website
    </Button>
  );
}

function SuccessContent({ data }) {
  if (!data) return null;

  var pageCount = data.pages_crawled || 0;
  var chunkCount = 0;
  if (data.text_chunks) chunkCount = data.text_chunks.length;
  else if (data.text_chunks_count) chunkCount = data.text_chunks_count;

  var stats = data.crawl_stats || {};
  var linksFound = stats.total_links_found || 0;
  var skipped = stats.skipped || 0;

  var pages = data.page_summaries || [];
  var facts = data.structured_facts || {};
  var factKeys = Object.keys(facts);

  return (
    <div className="space-y-4">
      <Alert>
        <CheckCircle className="w-4 h-4 text-emerald-600" />
        <AlertDescription>
          <span className="font-medium">Analisi completata</span>
          {' — '}{pageCount} pagine analizzate, {chunkCount} blocchi di testo estratti
          {linksFound > 0 && (
            <span className="text-slate-500 ml-1">
              ({linksFound} link trovati, {skipped} saltati)
            </span>
          )}
        </AlertDescription>
      </Alert>

      {pages.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Pagine analizzate</Label>
          <div className="bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-200">
            {pages.map(function(pg, idx) {
              return (
                <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={'page-summary-' + idx}>
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-800 truncate block">{pg.title || pg.url}</span>
                    <span className="text-xs text-slate-400 truncate block">{pg.url}</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-3 shrink-0">
                    <span className={'text-xs px-2 py-0.5 rounded-full ' + getPageColor(pg.page_type)}>
                      {pg.page_type}
                    </span>
                    <span className="text-xs text-slate-400">{pg.text_blocks_count} blocchi</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {factKeys.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Dati estratti dal sito</Label>
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-3">
            {factKeys.map(function(key) {
              var fact = facts[key];
              return (
                <div key={key} className="flex items-start justify-between" data-testid={'fact-' + key}>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{key.replace(/_/g, ' ')}</span>
                    <p className="text-sm text-slate-800 mt-0.5">{getFactValue(fact)}</p>
                  </div>
                  <span className={'text-xs px-2 py-0.5 rounded-full ' + getBadgeColor(fact)}>
                    {getBadgeLabel(fact)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">
            I campi manuali sopra (prezzi, indirizzo, nomi) hanno sempre priorità sui dati estratti dal sito.
          </p>
        </div>
      )}
    </div>
  );
}

export default function WebsiteKnowledgeCard(props) {
  var websiteUrl = props.websiteUrl;
  var setWebsiteUrl = props.setWebsiteUrl;
  var analyzing = props.analyzing;
  var analyzeStatus = props.analyzeStatus;
  var knowledgeData = props.knowledgeData;
  var onAnalyze = props.onAnalyze;

  return (
    <Card data-testid="card-website-knowledge">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Globe className="w-5 h-5 text-emerald-600" />
          <CardTitle>Website Knowledge</CardTitle>
        </div>
        <CardDescription>
          Inserisci l'URL del tuo sito web. EVA analizzerà i contenuti per rispondere alle domande dei clienti in modo naturale e preciso.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="website_url">URL del sito web</Label>
          <div className="flex space-x-2">
            <Input
              id="website_url"
              value={websiteUrl}
              onChange={function(e) { setWebsiteUrl(e.target.value); }}
              placeholder="https://www.tuosito.com"
              className="flex-1"
              data-testid="input-website-url"
            />
            <AnalyzeButton
              analyzing={analyzing}
              analyzeStatus={analyzeStatus}
              websiteUrl={websiteUrl}
              onAnalyze={onAnalyze}
            />
          </div>
          <p className="text-xs text-slate-500">
            EVA leggerà il sito per estrarre informazioni su servizi, prezzi, orari e altro. I tuoi campi manuali avranno sempre priorità.
          </p>
        </div>

        {analyzeStatus === 'success' && <SuccessContent data={knowledgeData} />}

        {analyzeStatus === 'error' && (
          <Alert variant="destructive">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Impossibile analizzare il sito. Verifica che l'URL sia corretto e raggiungibile.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
